import { createSignal, For, Match, Switch, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import theme, { palette } from '../../style/theme';
import { Heading } from '../../ui-components/heading/heading';
import { Grid } from '../../grid-system/grid/grid';
import clsx from 'clsx';
import { ExpandableContainer } from '../../ui-components/expandable-container/expandable-container';
import { Section } from '../../grid-system/section/section';
import { PageContext } from '../page/page-context-provider';
import { VideoData } from './videos-types';
import { VideoPostCard } from './video-post-card/video-post-card';
import { useLocation, useNavigate, useSearchParams } from '@solidjs/router';
import { ItemListSchema, mapVideosToItemList } from '../structured-data';

type TopCategory = {
    slug: string;
    name: string;
    children?: TopCategory[];
};

export const gridSettings = {
    container: {
        templateShorthand: [4, 4, 4],
    },
};

const extractCategories = (accCategories: TopCategory[], categories: TopCategory[]) => {
    let uniqueCategories = [...accCategories];

    categories.forEach((category) => {            
        if (!uniqueCategories.find((uc) => uc.slug === category.slug)) {
            uniqueCategories.push({
                slug: category.slug,
                name: category.name,
            });
        }

        if (category?.children && category?.children.length > 0) {
            uniqueCategories = [...extractCategories(uniqueCategories, category.children)];
        }
    });

    return uniqueCategories;
};

export const extractTopCategories = (videos: VideoData[]) => {
    const allCategories = videos.reduce((accCategories: TopCategory[], video) => {
        accCategories = [...extractCategories(accCategories, video.categories)];

        return accCategories;
    }, []);
    
    return allCategories;
};

export const createGroupedVideoCard = (video: VideoData) => {
    return createVideoCard(video, true);
};

const createVideoCard = (video: VideoData, inGrouping?: boolean) => {
    if (!video) {
        return <></>;
    }

    return (
        <VideoPostCard
            title={video?.video_title?.trim()}
            videoUrl={video?.video_link}
            inGrouping={inGrouping}
            isHealthcareProfessional={video?.isHealthcareProfessional}
            isVisibleForPublic={video?.isVisibleForPublic}
            permalink={video?.permalink}
            image={video?.image}
            ableToPlay={false}
        />
    );
};

export const VideosComponent = (props: VideosComponentProps) => {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { viewport, localize } = useContext(AppContext);
    const { withSidebarMenu } = useContext(PageContext);
    const [topCategories, setTopCategories] = createSignal<TopCategory[]>([]);
    const [selectedCategory, setSelectedCategory] = createSignal<TopCategory>({
        slug: searchParams.category || 'all',
        name: ''
    });

    const isMobile = () => viewport.width <= theme.breakpoints.MOBILE;

    const categoryName = () => {
        const category = topCategories().find((category) => {
            return category.slug === selectedCategory().slug;
        });
        
        if (!category) {
            return '';
        }

        return category.name;
    };

    const filteredVideos = () => {
        if (selectedCategory().slug === 'all') {
            return props.videos;
        }

        const matchCategory = (categories: TopCategory[], targetSlug: string) => {
            const foundMatch = categories.find((category) => {
                if (category.slug === targetSlug) {
                    return true;
                }

                if (category.children && category.children.length > 0) {
                    return matchCategory(category.children, targetSlug);
                }

                return false;
            });

            if (!foundMatch) {
                return false;
            }

            return true;
        };

        const matches = props.videos.filter((video) => {
            return matchCategory(video.categories, selectedCategory().slug);
        });
        
        return matches;
    };

    let expandableContainer: HTMLDivElement | undefined;
    const handleCategoryClick = (category: TopCategory | null) => {
        const categoryToUse = category || { slug: 'all', name: 'All' };
        setSelectedCategory(categoryToUse);
        expandableContainer?.click();
        navigate(`${location.pathname}?category=${categoryToUse.slug}`, { scroll: false });
    };

    const extractedTopCategories = extractTopCategories(props.videos);
    setTopCategories(extractedTopCategories);

    const itemListItems = () => mapVideosToItemList(filteredVideos() as any);

    const renderCategoriesList = () => {
        return (
            <ul class="flex items-center flex-wrap ml-[2rem] max-[480px]:ml-0 max-[480px]:block">
                <li
                    class={clsx(
                        'mr-[2rem] cursor-pointer list-none px-[1rem] pb-[0.5rem] pt-[0.3rem]',
                        'max-[480px]:ml-0 max-[480px]:border-t max-[480px]:border-gray max-[480px]:py-[1.5rem] max-[480px]:px-0',
                        selectedCategory() === null && 'underline decoration-primary'
                    )}
                    onClick={() => handleCategoryClick(null)}
                    aria-label={localize('show-all-categories', 'Show All Categories')}
                >
                    <Heading tag="h3" variant="tinyRed" noBlockSpacing={true}>
                        {localize('all', 'All')}
                    </Heading>
                </li>

                <For each={topCategories()}>
                    {(topCategory: TopCategory) => (
                        <li
                            class={clsx(
                                'mr-[2rem] cursor-pointer list-none px-[1rem] pb-[0.5rem] pt-[0.3rem]',
                                'max-[480px]:ml-0 max-[480px]:border-t max-[480px]:border-gray max-[480px]:py-[1.5rem] max-[480px]:px-0',
                                selectedCategory()?.slug === topCategory.slug && 'underline decoration-primary'
                            )}
                            onClick={() => handleCategoryClick(topCategory)}
                            aria-label={localize('show-category', 'Show Category')}
                        >
                            <Heading tag="h3" variant="tinyRed" noBlockSpacing={true}>
                                {topCategory.name}
                            </Heading>
                        </li>
                    )}
                </For>
            </ul>
        );
    };

    return (
        <>
            <ItemListSchema
                items={itemListItems()}
                listOrder="ItemListOrderDescending"
                name={localize('videos', 'Videos')}
            />
            <Section
                templateShorthand={[12]}
                widthType='bgFull'
                backgroundType='color'
                backgroundValue='white'
                customCss='z-index: 1;'
            >
                <Switch>
                    <Match when={!isMobile()}>
                        <div class="flex items-end relative py-[2.2rem]">
                            <div class="self-start min-w-[13rem]">
                                <Heading tag="h3" variant="medium" noBlockSpacing={true}>
                                    {localize('categories', 'Categories')}
                                </Heading>
                            </div>
                            {renderCategoriesList()}
                        </div>
                    </Match>

                    <Match when={isMobile()}>
                        <div class="relative py-[2.2rem]">
                            <ExpandableContainer
                                ref={expandableContainer}
                                initialOpen={false}
                                chevronColor={palette.primary}
                                isDropdown={true}
                                withBorder={true}
                                headingProps={{
                                    noBlockSpacing: true,
                                    tag: 'h3',
                                    variant: 'medium',
                                    children: categoryName() || localize('categories', 'Categories'),
                                }}
                            >
                                {renderCategoriesList()}
                            </ExpandableContainer>
                        </div>
                    </Match>
                </Switch>
            </Section>


            <Section templateShorthand={[12]} widthType='bgFull' isLayoutSection={withSidebarMenu() ? false : true}>
                <Grid 
                    templateShorthand={withSidebarMenu() ? [6,6] : [4,4,4]}
                    responsive={{mobile: [12], tablet: [12], smallDesktop: [6,6]}}
                >
                    <For each={filteredVideos()}>{(video: VideoData) => {
                        return createGroupedVideoCard(video);
                    }}
                    </For>
                </Grid>
            </Section>
        </>
    );
};
export type VideosComponentProps = {
    videos: VideoData[];
}
